import { useMemo } from 'react'
import type { JSX } from 'react'

import { Button, Dialog } from '@buffer-mono/popcorn'

export const ConfirmationDialog = ({
  count,
  channelName,
  postType,
  onConfirm,
  children,
}: {
  count: number
  channelName: string
  postType: 'post' | 'draft'
  onConfirm: () => void
  children: React.ReactNode
}): JSX.Element => {
  const title = useMemo(() => {
    if (count > 1) {
      return postType === 'post' ? 'Schedule posts?' : 'Save posts as drafts?'
    }
    if (count === 0) {
      return 'No posts to add'
    }
    return postType === 'post' ? 'Add post to queue?' : 'Save post as draft?'
  }, [count, postType])

  const description = useMemo(() => {
    if (count === 0) {
      return 'Please ensure there is at least one valid post to add.'
    }
    if (postType === 'post') {
      return (
        <>
          You are about to schedule{' '}
          <strong>
            {count} post{count > 1 ? 's' : ''}
          </strong>{' '}
          to <strong>{channelName}</strong>.<br />
          Any posts with errors are not going to be published.
        </>
      )
    }
    return (
      <>
        You are about to save{' '}
        <strong>
          {count} draft{count > 1 ? 's' : ''}
        </strong>{' '}
        for <strong>{channelName}</strong>.<br />
        Any posts with errors are not going to be saved.
      </>
    )
  }, [count, postType, channelName])

  return (
    <Dialog>
      <Dialog.Trigger>{children}</Dialog.Trigger>
      <Dialog.Content size="medium">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer>
          <Dialog.Close>
            <Button variant="tertiary" size="large">
              {count > 0 ? 'Cancel' : 'Close'}
            </Button>
          </Dialog.Close>
          {count > 0 && (
            <Dialog.Close>
              <Button variant="primary" size="large" onClick={onConfirm}>
                Confirm
              </Button>
            </Dialog.Close>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
